import { AutocompleteItem, FormAutocomplete } from "components/Forms/FormAutocomplete";
import { DocumentType } from "generated-graphql/graphql";
import { Controller, type Control } from "react-hook-form";

import type { DocumentMetadataFormData } from "../types";

import { RevertHelperLine } from "./RevertHelperLine";

export type DocumentTypeOption = AutocompleteItem<DocumentType>;

type DocumentTypeFieldProps = {
  control: Control<DocumentMetadataFormData>;
  documentTypeOptions: DocumentTypeOption[];
  originalDocumentType: DocumentType | null | undefined;
  needsReview: boolean;
};

/** Searchable Document Type picker with the AI-suggestion mark and "revert to original" line. */
export function DocumentTypeField({
  control,
  documentTypeOptions,
  originalDocumentType,
  needsReview,
}: DocumentTypeFieldProps) {
  return (
    <Controller
      name="documentType"
      control={control}
      rules={{ required: "Document type is required" }}
      render={({ field, fieldState }) => {
        const isSuggested = Boolean(field.value) && !fieldState.isDirty && needsReview;
        const showRevert = Boolean(originalDocumentType) && originalDocumentType !== field.value;
        return (
          <FormAutocomplete
            name={field.name}
            control={control}
            label="Document Type"
            autocompleteItems={documentTypeOptions}
            autocompleteProps={{ disableClearable: true }}
            suggestionProps={{ isSuggested }}
            helperText={
              showRevert && originalDocumentType ? (
                <RevertHelperLine
                  label="Original type"
                  displayValue={
                    documentTypeOptions.find((option) => option.value === originalDocumentType)
                      ?.label ?? originalDocumentType
                  }
                  tooltip="Click to restore the original document type"
                  onClick={() => field.onChange(originalDocumentType)}
                />
              ) : undefined
            }
          />
        );
      }}
    />
  );
}
